import { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useGlobalManualLocationOrder } from "../../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useUserSettingsContext } from "../../../contexts/Staffing/UserSettingsContext";
import { useWeekViewContext } from "../../../contexts/WeekView/WeekViewContext";
import { useHppdByDimension } from "../../../hooks/useHppdByDimension";
import { useJobs } from "../../../hooks/useJobs";
import { type DayItem, type DayStats, hppdTargetForDate, type OpenShiftRequest } from "../../../utils/models";
import { sortByLocationOrder } from "../../../utils/multiColumnSorting";
import { isPastDayWithPunches } from "../../../utils/pastDayStats";
import { resolveSectionRoles } from "../WeeklyGapView/FlatRowRenderer";
import type { RoleSection } from "../WeeklyGapView/useWeeklyGapView";
import { EMPTY_SECTIONS, useWeeklyGapView } from "../WeeklyGapView/useWeeklyGapView";
import {
	buildGapsJobTotals,
	distinctStaffEntries,
	type GapsDayFilter,
	type GapsJobTotal,
	type GapsPostedScope,
	type GapsStaffEntry,
	isRemovableStaffEntry,
	type KeyedRoleSection,
	keySections,
	narrowSections,
	postedRequestIdsForDay,
	shortfallsForWeek,
} from "./gapsDayModel";
import { buildScopedGapsDayModel, type GapsScopedShiftGroup, type GapsSectionScope, resolveSectionJob } from "./gapsGroupSheets";
import { postedRequestsForDay, staffingDayKey } from "./gapsRequests";
import type { GapsFilterState } from "./useGapsFilter";
import { useGapsFilter } from "./useGapsFilter";
import { useGapsVisibleWeek } from "./useGapsVisibleWeek";
import { useReportAmbiguousJobSections } from "./useReportAmbiguousJobSections";

/** The day's HPPD and the target it is measured against, as the Day Schedule header prints them. */
export type GapsDayHppd = { hppd: number; target: number };

export type GapsHomeModel = {
	/** The seven days the strip draws, in order. */
	weekDays: DayItem[];
	/** Which of them is selected, or `-1` while the week has not loaded the selected date. */
	dayIndex: number;
	/** One shortfall per day, same order as `weekDays`. */
	weekShortfalls: number[];
	/** The selected day's groups, already narrowed by the saved scope, each carrying its section's scope. */
	groups: GapsScopedShiftGroup[];
	/**
	 * The selected day's groups UNNARROWED — building-wide. For the actions the saved scope does not reach: the
	 * assign-unit sheet (a pickup approved from Open Shifts can land in a job the scope hides) and the call-in picker.
	 */
	dayGroups: GapsScopedShiftGroup[];
	/** The day's staff for the call-in picker: unnarrowed (P2-11), each shift once (`distinctStaffEntries`). */
	callInStaff: GapsStaffEntry[];
	/** The day's posted requests, building-wide — the Posted tab's list before `isPostedRequestInScope` narrows it. */
	dayPostedRequests: OpenShiftRequest[];
	/** Per-job totals for the gap line. */
	jobTotals: GapsJobTotal[];
	/** Which of the day's posted requests the saved scope keeps, for the Posted tab. */
	postedScope: GapsPostedScope;
	/**
	 * Whether the week's grouping is final. Until the saved settings arrive the provider answers with its
	 * defaults (Role grouping, no HPPD split), so everything built from the gap view is provisional and the
	 * page draws none of it — the same reason `dayHppd` waits.
	 */
	isWeekReady: boolean;
	/** `isWeekReady`, and the selected day is inside the loaded week: the groups, gap line and tab counts are real. */
	isDayReady: boolean;
	/**
	 * The selected day is before today and has punches, so everything on it — groups, gaps, stats, HPPD — is what was
	 * punched, the desktop Day view's Actual (APP-886). The screen marks it so it is never read as the schedule.
	 */
	isDayActual: boolean;
	/**
	 * The selected day is before today, punched or not: an open shift posted for it could never be picked up, so nothing
	 * on it offers Post (APP-886). Adding someone stays where the day is not punched, as the desktop allows.
	 */
	isDayPast: boolean;
	/** The selected day's stats — census, PPD, cost; the punched ones on an actual day. Absent until the week has loaded. */
	dayStats: DayStats | undefined;
	/** The selected day's HPPD and target — NOT `dayStats.hppd` (see `useGapsHomeModel`). Absent until the week and the saved settings have loaded. */
	dayHppd: GapsDayHppd | undefined;
	/** Every job / location / window the UNNARROWED day offers, for the filter sheet's chip rows. */
	available: { jobLabels: string[]; locationNames: string[]; timeLabels: string[] };
	/** The saved scope, resolved here because it needs `available` to tell "all of them" from "a filter". */
	filterState: GapsFilterState;
};

/** A past day with punches shows them on the phone, as the desktop Day view opens such a day on Actual (APP-886). */
const PAST_DAYS_SHOW_PUNCHES = true;

/** Distinct values in first-seen order — the gap view already sorted what it produced. */
const distinct = (values: string[]): string[] => Array.from(new Set(values));

/**
 * The scope the add and post sheets read off a group's section (`GapsSectionScope`): the desktop grid's own
 * `resolveSectionRoles`, and for a job section the job looked up in the SAME enabled-job list `useWeeklyGapView`
 * named its sections from (see `resolveSectionJob`) — or, for a name two enabled jobs share, the ambiguity instead.
 */
const useSectionScopeResolver = (): ((section: RoleSection) => GapsSectionScope) => {
	const { chosenDepartment } = useHospitalData();
	const enabledJobs = useJobs();
	return useCallback(
		(section: RoleSection): GapsSectionScope => ({
			roles: resolveSectionRoles(section, chosenDepartment.role_groups),
			...resolveSectionJob(section, enabledJobs),
		}),
		[chosenDepartment.role_groups, enabledJobs],
	);
};

/**
 * The selected day's posted requests, building-wide. The day is decided by `postedRequestsForDay`, the Posted tab's
 * own staffing-day rule.
 */
const useDayPostedRequests = (selectedDate: DateTime): OpenShiftRequest[] => {
	const { staffRequests } = useRequestsContext();
	const { chosenDepartment } = useHospitalData();
	const { dayStart, scheduled_shift_delta_minutes } = chosenDepartment;
	const dayKey = staffingDayKey(selectedDate);

	return useMemo<OpenShiftRequest[]>(
		() => postedRequestsForDay(staffRequests, dayKey, { dayStart, scheduled_shift_delta_minutes }),
		[staffRequests, dayKey, dayStart, scheduled_shift_delta_minutes],
	);
};

/**
 * Everything the gaps-first home draws for one day.
 *
 * Calls `useWeeklyGapView` ONCE for the whole week and slices it. The week strip needs a shortfall
 * for all seven days and the list needs one day in full; asking per day would run the same expensive
 * computation seven times over the same `weekViewData`.
 *
 * The grouping mode is read from the user's staffing settings rather than re-derived from the job
 * flag and the department's role groups. `UserSettingsProvider` already resolves that — including
 * forcing `Jobs` when job management is on — and a second derivation here is how the mobile and
 * desktop screens would come to group the same department differently.
 */
export const useGapsHomeModel = (selectedDate: DateTime): GapsHomeModel => {
	const weekViewContext = useWeekViewContext();
	const { isDynamicWeekStartEnabled } = useGapsVisibleWeek();
	// The same seven days the desktop weekly gap view builds from (`WeeklyGapView`): its visible week when weeks
	// start on the user's chosen day, the Sunday data week otherwise.
	const weekViewData = isDynamicWeekStartEnabled ? weekViewContext.visibleWeekViewData : weekViewContext.weekViewData;
	const { settings, settingsResolved } = useUserSettingsContext();
	const { chosenDepartment } = useHospitalData();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const sections: RoleSection[] = useWeeklyGapView(weekViewData, settings.weeklyGroupingType, EMPTY_SECTIONS, PAST_DAYS_SHOW_PUNCHES).roleSections;
	useReportAmbiguousJobSections(sections);

	const dayIndex = useMemo<number>(() => {
		const selectedISO = selectedDate.toISODate();
		return weekViewData.findIndex((day) => day.date.toISODate() === selectedISO);
	}, [weekViewData, selectedDate]);
	const day: DayItem | undefined = dayIndex < 0 ? undefined : weekViewData[dayIndex];
	// The same test the gap view used to swap in the punches, so the stats and the groups always describe the same side.
	const isDayActual = useMemo<boolean>(() => day !== undefined && isPastDayWithPunches(day.date, day.shiftsActual, DateTime.now()), [day]);
	const isDayPast = useMemo<boolean>(() => day !== undefined && day.date < DateTime.now().startOf("day"), [day]);
	// DEFAULT: none while the day is outside the loaded week — `dayStats` and `dayHppd` stay undefined then.
	const dayShifts = day === undefined ? [] : isDayActual ? day.shiftsActual : day.shifts;
	const activeStats: DayStats | undefined = day === undefined ? undefined : isDayActual ? day.dayStatsActual : day.dayStats;

	// HPPD exactly as the Day Schedule header computes it. `DayScheduleHeader` prints `useHppdByDimension` over the
	// day's shifts and `hppdTargetForDate`, NOT `DayStats.hppd` / `wantedHours`: they are different calculations —
	// split by role, the header's total is the sum of each role's ROUNDED HPPD — so reading `dayStats` left the
	// phone a rounding step off the header (2.81 against 2.80 on CenterAtLowry, 2026-09-24).
	// On an actual day the header's Actual figure: the punched shifts over the punched day's census.
	// DEFAULT: a day the week has not loaded has no shifts and no census, which the hook reads as HPPD 0. Nothing
	// prints it — `dayHppd` stays undefined until the day exists — and the header's own input is `?.shifts || []`.
	const hppdByDimension = useHppdByDimension(dayShifts, activeStats?.census ?? 0, selectedDate);
	// Held back until the saved settings arrive. Before then the provider answers with its defaults — no HPPD split —
	// so the hook's first total is the UNSPLIT one, and it flipped to the header's figure ~250ms later (2.81 → 2.80).
	// Printing nothing for that moment beats printing a number the header will never show.
	const dayHppd = useMemo<GapsDayHppd | undefined>(
		() =>
			day === undefined || !settingsResolved
				? undefined
				: { hppd: hppdByDimension.totalHppd, target: hppdTargetForDate(chosenDepartment.hppd_target, selectedDate.weekday) },
		[day, settingsResolved, hppdByDimension.totalHppd, chosenDepartment.hppd_target, selectedDate],
	);

	const available = useMemo(
		() => ({
			jobLabels: distinct(sections.map((section) => section.shortRole)),
			timeLabels: distinct(sections.flatMap((section) => section.timeTitles.map((timeTitle) => timeTitle.label))),
			// The unassigned pseudo-location is never offered as a filter chip: it is not a place, and it
			// is exempt from the filter anyway (see `narrowSections`). Sorted, not first-seen: each window lists its
			// units in the manual order, but a unit that only runs in a later window would otherwise land last.
			locationNames: sortByLocationOrder(
				distinct(sections.flatMap((section) => section.timeTitles.flatMap((timeTitle) => timeTitle.locations.map((location) => location.locationName)))).filter(
					(name) => name !== "",
				),
				customStringOrderMap,
			),
		}),
		[sections, customStringOrderMap],
	);

	// Resolved here rather than by the page because it needs `available`: a stored scope listing every
	// unit is "show everything", and only the day's own data knows how many units there are.
	const filterState = useGapsFilter(available);
	const filter: GapsDayFilter = filterState.filter;

	// Keyed before anything narrows them, so a group has one key in the narrowed and the building-wide list alike.
	const keyedSections = useMemo<KeyedRoleSection[]>(() => keySections(sections), [sections]);
	const narrowed = useMemo<KeyedRoleSection[]>(() => narrowSections(keyedSections, filter, weekViewData.length), [keyedSections, filter, weekViewData.length]);

	// The strip is narrowed too: a supervisor scoped to three carts is asking "which day am I short on
	// MY carts", and a building-wide number under their own filtered list would contradict it.
	const weekShortfalls = useMemo<number[]>(() => shortfallsForWeek(narrowed, weekViewData.length), [narrowed, weekViewData.length]);

	const scopeForSection = useSectionScopeResolver();
	const groups = useMemo<GapsScopedShiftGroup[]>(
		() => (dayIndex < 0 ? [] : buildScopedGapsDayModel(narrowed, dayIndex, scopeForSection)),
		[narrowed, dayIndex, scopeForSection],
	);
	const dayGroups = useMemo<GapsScopedShiftGroup[]>(
		() => (dayIndex < 0 ? [] : buildScopedGapsDayModel(keyedSections, dayIndex, scopeForSection)),
		[keyedSections, dayIndex, scopeForSection],
	);
	// Only people the removal can take off: on a punched day, someone with a punch is worked time, not a call-in.
	const callInStaff = useMemo<GapsStaffEntry[]>(() => distinctStaffEntries(dayGroups).filter(isRemovableStaffEntry), [dayGroups]);
	const dayPostedRequests = useDayPostedRequests(selectedDate);
	const jobTotals = useMemo<GapsJobTotal[]>(() => (dayIndex < 0 ? [] : buildGapsJobTotals(narrowed, dayIndex)), [narrowed, dayIndex]);
	// Placed is read off the WHOLE day and in-scope off the narrowed one, so a request the filter excluded
	// is told apart from one that was never placed anywhere (see `isPostedRequestInScope`).
	const postedScope = useMemo<GapsPostedScope>(
		() =>
			dayIndex < 0
				? { placed: new Set(), inScope: new Set() }
				: { placed: postedRequestIdsForDay(sections, dayIndex), inScope: postedRequestIdsForDay(narrowed, dayIndex) },
		[sections, narrowed, dayIndex],
	);

	return {
		weekDays: weekViewData,
		dayIndex,
		isWeekReady: settingsResolved,
		isDayReady: settingsResolved && dayIndex >= 0,
		weekShortfalls,
		groups,
		dayGroups,
		callInStaff,
		dayPostedRequests,
		jobTotals,
		postedScope,
		isDayActual,
		isDayPast,
		// Read from the day the provider already built rather than calling `calculateDayStats` again:
		// `DayItem.dayStats` / `dayStatsActual` is what the Day Schedule header prints, so the two cannot disagree.
		dayStats: activeStats,
		dayHppd,
		available,
		filterState,
	};
};
